import { useState } from 'react';
import { ArrowLeft, Loader2, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { POSTAL_CODE_MAX_LENGTH, sanitizePostalCodeInput } from '@/lib/postal-code';
import { FormSectionCard } from '@/components/molecules';
import { AddressSelector } from '@/components/organisms';
import { CompanyLocationSelector } from '@/components/organisms/CompanyLocationSelector';
import { BillToSelector } from '@/components/organisms/contacts/BillToSelector';
import { EstimateLiteLineItemsSection } from '@/components/organisms/estimates/EstimateLiteLineItemsSection';
import { EstimateLiteSummaryCard } from '@/components/organisms/estimates/EstimateLiteSummaryCard';
import { TaxResolutionSection } from '@/components/tax/TaxResolutionSection';
import { useCreateEstimate } from '@/hooks/estimates/useCreateEstimate';
import { useEstimateCustomerState } from '@/hooks/estimates/useEstimateCustomerState';
import { useEstimateCustomerTaxSync } from '@/hooks/estimates/useEstimateCustomerTaxSync';
import { useEstimateLiteLineItems } from '@/hooks/estimates/useEstimateLiteLineItems';
import { useEstimatePrefillContact } from '@/hooks/estimates/useEstimatePrefillContact';
import { useEstimateTaxResolution } from '@/hooks/estimates/useEstimateTaxResolution';
import { buildEstimateLitePayload } from './_shared';

export function GuidedEstimateWizard({ prefillContactId }: { prefillContactId?: string }) {
  const router = useRouter();
  const createEstimate = useCreateEstimate();

  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 3;

  const {
    contactId,
    billToId,
    setBillToId,
    customerName,
    customerEmail,
    customerPhone,
    primaryContactName,
    primaryContactTitle,
    contactAddresses,
    selectedAddressIndex,
    setSelectedAddressIndex,
    showAllAddresses,
    setShowAllAddresses,
    saStreet,
    setSaStreet,
    saCity,
    setSaCity,
    saState,
    setSaState,
    saZip,
    setSaZip,
    applyAddress,
    selectContact,
    clearContact,
  } = useEstimateCustomerState();

  const {
    taxResolution,
    taxLoading,
    resolveTax,
    handleZipChange,
    clearPendingTaxResolution,
    setTaxResolution,
  } = useEstimateTaxResolution({ city: saCity, state: saState, zipDebounceMs: 600 });

  const { applyAddressWithTax, selectContactWithTax, clearContactWithTax } =
    useEstimateCustomerTaxSync({
      applyAddress,
      selectContact,
      clearContact,
      resolveTax,
      clearPendingTaxResolution,
      setTaxResolution,
    });

  const [saving, setSaving] = useState(false);

  useEstimatePrefillContact({
    prefillContactId,
    isEditMode: false,
    onSelectContact: selectContactWithTax,
    isCustomerAlreadySet: () => Boolean(contactId),
  });

  const {
    lineItems,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    showSearchResults,
    setShowSearchResults,
    searchRef,
    addItem,
    removeLineItem,
    updateQuantity,
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    grandTotal,
  } = useEstimateLiteLineItems(taxResolution);

  async function saveAsDraft() {
    setSaving(true);
    try {
      const payload = buildEstimateLitePayload({
        contactId,
        billToId,
        customerName,
        customerEmail,
        customerPhone,
        primaryContactName,
        primaryContactTitle,
        saStreet,
        saCity,
        saState,
        saZip,
        lineItems,
      });
      const created = (await createEstimate.mutateAsync(payload)) as { id?: string; _id?: string };
      const newId = created?.id ?? created?._id;
      if (newId)
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({
          to: '/dashboard/estimates/$id',
          params: { id: newId },
        });
      // @ts-expect-error — search required by TanStack Router v1.168
      else void router.navigate({ to: '/dashboard/estimates' });
    } catch (err: unknown) {
      toast.error((err as Error)?.message ?? 'Failed to create estimate');
    } finally {
      setSaving(false);
    }
  }

  async function saveAndEdit() {
    setSaving(true);
    try {
      const payload = buildEstimateLitePayload({
        contactId,
        billToId,
        customerName,
        customerEmail,
        customerPhone,
        primaryContactName,
        primaryContactTitle,
        saStreet,
        saCity,
        saState,
        saZip,
        lineItems,
      });
      const created = (await createEstimate.mutateAsync(payload)) as { id?: string; _id?: string };
      const newId = created?.id ?? created?._id;
      if (newId)
        void router.navigate({
          to: '/dashboard/estimates/new',
          search: { edit: newId } as never,
        });
      else void router.navigate({ to: '/dashboard/estimates', search: {} as never });
    } catch (err: unknown) {
      toast.error((err as Error)?.message ?? 'Failed to create estimate');
    } finally {
      setSaving(false);
    }
  }

  const stepLabels = ['Customer', 'Address & Items', 'Finish'];

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-4 sm:p-6 space-y-5 max-w-2xl mx-auto w-full pb-10">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              if (step > 1) setStep((s) => s - 1);
              // @ts-expect-error — search required by TanStack Router v1.168
              else void router.navigate({ to: '/dashboard/estimates/new' });
            }}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Guided Estimate</h1>
            <p className="text-sm text-muted-foreground">
              Step {step} of {TOTAL_STEPS} — {stepLabels[step - 1]}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-1.5 flex-1">
              <div
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i < step ? 'bg-primary' : 'bg-muted'}`}
              />
              {i < stepLabels.length - 1 && null}
            </div>
          ))}
        </div>

        {/* Step 1 — Customer */}
        {step === 1 && (
          <FormSectionCard className="space-y-3">
            <p className="font-semibold text-sm">
              Who is this estimate for? <span className="text-destructive">*</span>
            </p>
            <CompanyLocationSelector
              selected={
                contactId
                  ? {
                      id: contactId,
                      name: customerName,
                      primaryEmail: customerEmail || undefined,
                      primaryPhone: customerPhone || undefined,
                      primaryContactName: primaryContactName || undefined,
                      primaryContactTitle: primaryContactTitle || undefined,
                    }
                  : null
              }
              onSelect={selectContactWithTax}
              onClear={clearContactWithTax}
              placeholder="Search customer..."
            />
            {contactId ? (
              <BillToSelector contactId={contactId} value={billToId} onChange={setBillToId} />
            ) : null}
            <Button className="w-full" disabled={!contactId} onClick={() => setStep(2)}>
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </FormSectionCard>
        )}

        {/* Step 2 — Address & Line Items */}
        {step === 2 && (
          <div className="space-y-4">
            <FormSectionCard className="space-y-3">
              <p className="font-semibold text-sm">
                Service Address <span className="text-destructive">*</span>
              </p>

              <AddressSelector
                title="Service Address"
                mode={contactAddresses.length > 0 ? 'select' : 'manual'}
                addresses={contactAddresses}
                visibleAddresses={
                  showAllAddresses ? contactAddresses : contactAddresses.slice(0, 4)
                }
                selectedIndex={selectedAddressIndex}
                contactSelected={Boolean(contactId)}
                showAll={showAllAddresses}
                maxVisibleAddresses={4}
                emptyContactMessage="Select a customer first to see their addresses."
                emptyAddressMessage="This contact has no saved addresses. Enter one manually."
                onToggleMode={() => {}}
                onSelectAddress={(index, address) => {
                  setSelectedAddressIndex(index);
                  applyAddressWithTax(address);
                }}
                onToggleShowAll={() => setShowAllAddresses((value) => !value)}
                renderManual={
                  <div className="space-y-2">
                    <Input
                      placeholder="Street"
                      value={saStreet}
                      onChange={(event) => setSaStreet(event.target.value)}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="City"
                        value={saCity}
                        onChange={(event) => setSaCity(event.target.value)}
                      />
                      <Input
                        placeholder="ST"
                        maxLength={2}
                        value={saState}
                        onChange={(event) =>
                          setSaState(event.target.value.toUpperCase().slice(0, 2))
                        }
                      />
                      <Input
                        placeholder="ZIP / Postal"
                        maxLength={POSTAL_CODE_MAX_LENGTH}
                        value={saZip}
                        onChange={(event) =>
                          handleZipChange(sanitizePostalCodeInput(event.target.value), setSaZip)
                        }
                      />
                    </div>
                  </div>
                }
              />

              {(taxLoading || taxResolution) && (
                <TaxResolutionSection resolution={taxResolution} loading={taxLoading} />
              )}
            </FormSectionCard>

            <EstimateLiteLineItemsSection
              lineItems={lineItems}
              lineItemPreviews={lineItemPreviews}
              searchQuery={searchQuery}
              searchResults={searchResults}
              searchLoading={searchLoading}
              showSearchResults={showSearchResults}
              searchRef={searchRef}
              subtotal={subtotal}
              taxTotal={taxTotal}
              taxBreakdown={taxBreakdown}
              grandTotal={grandTotal}
              taxResolved={Boolean(taxResolution)}
              zipValue={saZip}
              onSearchQueryChange={setSearchQuery}
              onSearchFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              onAddItem={addItem}
              onRemoveItem={removeLineItem}
              onUpdateQuantity={updateQuantity}
            />

            <Button className="w-full" disabled={lineItems.length === 0} onClick={() => setStep(3)}>
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 3 — Finish */}
        {step === 3 && (
          <FormSectionCard className="space-y-4">
            <div className="text-center space-y-1 py-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mx-auto">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <p className="font-semibold text-base mt-3">Almost done!</p>
              <p className="text-sm text-muted-foreground">
                Your estimate is ready. Do you want to configure advanced fields now?
              </p>
            </div>

            <EstimateLiteSummaryCard
              customerName={customerName}
              addressLine={saStreet ? `${saStreet}, ${saCity}` : undefined}
              itemCount={lineItems.length}
              subtotal={subtotal}
              taxTotal={taxTotal}
              taxBreakdown={taxBreakdown}
              grandTotal={grandTotal}
            />

            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" disabled={saving} onClick={saveAsDraft}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save as Draft
              </Button>
              <Button disabled={saving} onClick={saveAndEdit}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Configure Advanced
              </Button>
            </div>
          </FormSectionCard>
        )}
      </div>
    </div>
  );
}
